import { isPossiblePhoneNumber } from 'react-phone-number-input';

export function empty(v: string | number | null | undefined): boolean {
    return Boolean(typeof v === 'string' ? v.trim() : v) === false;
}

export function isValidOption(options: { label: string; value: any }[], v: any): boolean {
    return Boolean(options.some((o) => o.value === v));
}

export function between(v: number, min: number, max: number): boolean {
    return v >= min && v <= max;
}

export function hasEnoughContent(v: any, minLength = 1): boolean {
    return typeof v === 'string' && v.trim().length >= minLength;
}

export function isValidEmail(v: string): boolean {
    return /^.+@.+\..+$/.test(v);
}

export function isValidPhone(v: string | null | undefined): boolean {
    return Boolean(v && isPossiblePhoneNumber(v));
}

export function isBefore(v: number | Date, date: number | Date): boolean {
    return (
        (date instanceof Date ? date.getTime() : date) - (v instanceof Date ? v.getTime() : v) >= 0
    );
}

export function isNumber(v: string): boolean {
    return /^-?[0-9]+$/.test(v);
}

export function isPositiveInteger(v: string): boolean {
    return /^[0-9]+$/.test(v);
}

export function isFloat(v: string, precision?: number): boolean {
    return new RegExp(`^-?[0-9]+(\\.[0-9]{0,${precision || ''}})?$`).test(v);
}

export function isAlphanumeric(v: string | number | null | undefined): boolean {
    return (!!v || v === 0) && /^[0-9A-Za-z]+$/.test(`${v}`);
}

export function isValidDate(v: string | number | Date | undefined | null): boolean {
    return (
        Boolean(v) && (v instanceof Date || !isNaN(new Date(v as string | number | Date).getTime()))
    );
}

const exampleUsers = ['your_username', 'your_profile', 'example'];

export function isValidLinkedInUrl(url: string, allowMissingProtocol = true): boolean {
    try {
        if (allowMissingProtocol && !url.startsWith('http')) {
            url = `https://${url}`;
        }

        const u = new URL(url);
        if (u.host !== 'linkedin.com' && u.host !== 'www.linkedin.com') {
            return false;
        }

        if (!u.pathname.startsWith('/in/')) {
            return false;
        }

        const username = trimPrefix(trimSuffix(u.pathname.trim(), '/'), '/in/');

        return !(
            !username ||
            username.includes('/') ||
            exampleUsers.includes(username.toLowerCase())
        );
    } catch {
        return false;
    }
}

export function isValidCompanyLinkedInUrl(url: string, allowMissingProtocol = true): boolean {
    try {
        if (allowMissingProtocol && !url.startsWith('http')) {
            url = `https://${url}`;
        }

        const u = new URL(url);
        if (u.host !== 'linkedin.com' && u.host !== 'www.linkedin.com') {
            return false;
        }

        if (!u.pathname.startsWith('/company/') && !u.pathname.startsWith('/school/')) {
            return false;
        }

        const username = u.pathname.startsWith('/company/')
            ? trimPrefix(trimSuffix(u.pathname.trim(), '/'), '/company/')
            : trimPrefix(trimSuffix(u.pathname.trim(), '/'), '/school/');

        return !(
            !username ||
            username.includes('/') ||
            exampleUsers.includes(username.toLowerCase())
        );
    } catch {
        return false;
    }
}

export function isValidGitHubUrl(url: string, allowMissingProtocol = true): boolean {
    try {
        if (allowMissingProtocol && !url.startsWith('http')) {
            url = `https://${url}`;
        }

        const u = new URL(url);
        if (u.host !== 'github.com' && u.host !== 'www.github.com') {
            return false;
        }

        const username = trimPrefix(trimSuffix(u.pathname.trim(), '/'), '/');

        return !(
            !username ||
            username.includes('/') ||
            exampleUsers.includes(username.toLowerCase())
        );
    } catch {
        return false;
    }
}

export function isValidGrainUrl(url: string): boolean {
    const pattern =
        /https:\/\/grain\.com\/share\/(recording|story)\/\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\/\w{40}/;
    return pattern.test(url);
}

export const VALID_SCHEDULING_DOMAINS = [
    'calendly.com',
    'fantastical.app',
    'app.superpowered.me',
    'getclockwise.com',
    'cal.frontapp.com',
    'app.reclaim.ai',
    'meetings.hubspot.com',
    'calendar.app.google',
    'calendar.google.com',
    'app.usemotion.com',
    'cal.com',
    'scheduler.zoom.us',
    'app.calendarbridge.com',
    'calendar.notion.so',
    'outlook.office.com',
    'tidycal.com',
    'calendar.superhuman.com',
    'superhuman.com',
];

export function isValidSchedulingUrl(url: string, allowMissingProtocol = true): boolean {
    try {
        if (allowMissingProtocol && !url.startsWith('http')) {
            url = `https://${url}`;
        }

        const u = new URL(url);
        let host = u.host;
        if (host.startsWith('www.')) {
            host = host.substring(4);
        }

        if (!VALID_SCHEDULING_DOMAINS.includes(host)) {
            return false;
        }

        return u.pathname.replace(/\//gi, '').length > 1;
    } catch {
        return false;
    }
}

export function isValidUrl(url: string, allowMissingProtocol = true): boolean {
    try {
        if (allowMissingProtocol && !url.startsWith('http')) {
            url = `http://${url}`;
        }

        const u = new URL(url);
        return /.+\..+/.test(u.host);
    } catch {
        return false;
    }
}

export function isValidName(name: string): boolean {
    return /^\p{L}[\p{L} ,.'-]+$/u.test(name);
}

export function isValidIANATimezone(tz: string | undefined | null): boolean {
    if (!tz?.trim()) {
        return false;
    }
    try {
        Intl.DateTimeFormat(undefined, { timeZone: tz.trim() });
        return true;
    } catch {
        return false;
    }
}

function trimPrefix(str: string, prefix: string): string {
    if (str?.startsWith(prefix)) {
        return str.slice(prefix.length);
    }
    return str;
}

function trimSuffix(str: string, suffix: string): string {
    if (str?.endsWith(suffix)) {
        return str.slice(0, str.length - suffix.length);
    }
    return str;
}

// Keep this in sync with platform/startup_onboard.go
export const emailProviders = [
    'googlemail',
    'gmail',
    'yahoo',
    'yahoofs',
    'yahoomail',
    'hotmail',
    'microsoft',
    'outlook',
    'live',
    'icloud',
    'aol',
    'protonmail',
    'mail',
    'fastmail',
    'yandex',
    'hey',
];

export function isBusinessEmail(value: string): boolean {
    if (!value) return false;
    const domain = value.split('@').pop()!;
    return !emailProviders.find((p) => domain.startsWith(`${p}.`));
}

const DOMAIN_REGEX = /^[a-zA-Z0-9-_]+(\.[a-zA-Z]+)+$/;

export function isValidDomain(domain: string): boolean {
    return DOMAIN_REGEX.test(domain);
}

export function normalizeEmail(str: string = ''): string {
    str = str.trim().toLowerCase();
    while (str.endsWith('.')) {
        str = str.slice(0, str.length - 2);
    }

    const parts = str.split('@');
    if (parts.length === 2 && (parts[1] === 'gmail.com' || parts[1] === 'googlemail.com')) {
        parts[1] = 'gmail.com';
        parts[0] = parts[0].replaceAll('.', '').split('+')[0];
    }

    return parts.join('@');
}

const ALLOWED_COUNTRIES: Record<string, boolean> = {
    AR: true, // Argentina
    BO: true, // Bolivia
    BR: true, // Brazil
    BZ: true, // Belize
    CL: true, // Chile
    CO: true, // Colombia
    CR: true, // Costa Rica
    DO: true, // Dominican Republic
    EC: true, // Ecuador
    GT: true, // Guatemala
    HN: true, // Honduras
    MX: true, // Mexico
    NI: true, // Nicaragua
    PA: true, // Panama
    PE: true, // Peru
    PR: true, // Puerto Rico
    PY: true, // Paraguay
    SV: true, // El Salvador
    UY: true, // Uruguay
};

export function isAllowedCountry(country: string): boolean {
    return Object.hasOwn(ALLOWED_COUNTRIES, country);
}
